// src/pages/services/Cruise.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const Cruise = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "The Mediterranean cruise arranged by Excellent Travels was the highlight of our year. Every detail was perfectly planned, from the cabin selection to the shore excursions.",
            name: "Robert & Lisa Wilson",
            location: "Boston, USA",
            avatar: "/images/testimonials/person7.jpg"
        },
        {
            content: "As first-time cruisers, we were a bit nervous, but the team at Excellent Travels guided us through everything. The cruise exceeded all our expectations!",
            name: "James & Olivia Parker",
            location: "Sydney, Australia",
            avatar: "/images/testimonials/person8.jpg"
        }
    ];

    const faqs = [
        {
            question: "What's included in a typical cruise package?",
            answer: "Most cruise packages include accommodation, meals at main dining venues, entertainment, and access to onboard facilities like pools and fitness centers. Additional costs typically include specialty dining, alcoholic beverages, shore excursions, spa treatments, and gratuities."
        },
        {
            question: "How do I choose the right cruise for me?",
            answer: "Consider factors like destination, cruise duration, ship size and amenities, budget, and travel style. Our cruise specialists can help match you with the perfect cruise based on your preferences and interests."
        },
        {
            question: "Do I need a passport for a cruise?",
            answer: "For international cruises, a passport is typically required. For closed-loop cruises (beginning and ending at the same U.S. port), U.S. citizens may be able to travel with a birth certificate and government-issued photo ID, but we strongly recommend having a passport for any international travel."
        },
        {
            question: "What should I pack for a cruise?",
            answer: "Pack according to your destination's climate, including casual daywear, formal attire for special evenings, swimwear, comfortable walking shoes for excursions, and any necessary medications. We provide a detailed packing list tailored to your specific cruise itinerary."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="cruise" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1548574505-5e239809ee19?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                    alt="Luxury Cruise"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Cruise Vacations</h1>
                    <p className="service-subtitle">
                        Luxury cruise packages to destinations around the globe.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/2942/2942083.png"
                alt="Cruise Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2942/2942083.png" alt="Cruise Icon" className="section-icon" />
                        Set Sail on an Unforgettable Journey
                    </h2>
                    <p>
                        Experience the ultimate in relaxation and adventure with our premium cruise packages.
                        At Excellent Travels, we partner with the world's leading cruise lines to offer you
                        exceptional voyages to breathtaking destinations across the seven seas.
                    </p>
                    <p>
                        Our cruise vacation services include:
                    </p>
                    <ul>
                        <li>Access to all major cruise lines and luxury vessels</li>
                        <li>Exclusive deals and promotions not available elsewhere</li>
                        <li>Customized itineraries to match your interests</li>
                        <li>Pre and post-cruise accommodations and transfers</li>
                        <li>Curated shore excursions for authentic local experiences</li>
                        <li>Special occasion celebrations and group bookings</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1599640842225-85d111c60e6b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                            alt="Luxury Cruise Ship"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Experience luxury and comfort aboard world-class cruise ships</div>
                    </div>

                    <p>
                        Whether you're dreaming of the crystal-clear waters of the Caribbean, the historic ports of the Mediterranean,
                        the majestic glaciers of Alaska, or the exotic landscapes of Asia, we have the perfect cruise for you.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2942/2942076.png" alt="Destinations Icon" className="section-icon" />
                        Popular Cruise Destinations
                    </h2>
                    <p>
                        Explore some of our most sought-after cruise destinations:
                    </p>
                    <ul>
                        <li><strong>Caribbean & Bahamas:</strong> Sun-drenched beaches, vibrant cultures, and tropical paradise</li>
                        <li><strong>Mediterranean:</strong> Ancient history, stunning coastlines, and world-class cuisine</li>
                        <li><strong>Alaska:</strong> Breathtaking wilderness, wildlife, and natural wonders</li>
                        <li><strong>Northern Europe:</strong> Fjords, historic cities, and spectacular scenery</li>
                        <li><strong>Asia & South Pacific:</strong> Exotic ports, diverse cultures, and pristine islands</li>
                        <li><strong>South America:</strong> Vibrant cities, natural wonders, and rich heritage</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/3774/3774278.png" alt="Cruise Types Icon" className="section-icon" />
                        Types of Cruises
                    </h2>
                    <p>
                        We offer a variety of cruise experiences to suit every traveler:
                    </p>
                    <ul>
                        <li><strong>Luxury Cruises:</strong> Unparalleled service, gourmet dining, and elegant accommodations</li>
                        <li><strong>Family Cruises:</strong> Kid-friendly activities, family accommodations, and entertainment for all ages</li>
                        <li><strong>Adventure Cruises:</strong> Expedition-style voyages to remote destinations</li>
                        <li><strong>River Cruises:</strong> Intimate vessels navigating the world's most scenic waterways</li>
                        <li><strong>Theme Cruises:</strong> Specialized voyages focused on interests like culinary, music, or wellness</li>
                    </ul>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/2942/2942083.png"
                        alt="Cruise Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Book Your Cruise With Us</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Price Icon" />
                            </span>
                            Best price guarantee
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2942/2942076.png" alt="Credits Icon" />
                            </span>
                            Exclusive onboard credits
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2942/2942083.png" alt="Expert Icon" />
                            </span>
                            Expert cruise consultants
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Insurance Icon" />
                            </span>
                            Complimentary travel insurance options
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="Payment Icon" />
                            </span>
                            Flexible payment plans
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Support Icon" />
                            </span>
                            24/7 support before, during, and after your cruise
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/3774/3774278.png" alt="Service Icon" />
                            </span>
                            Personalized service and recommendations
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Set Sail?</h2>
                <p>
                    Contact our cruise specialists today for exclusive pricing and to start planning your perfect voyage.
                </p>
                <Link to="/contact" className="cta-button">
                    Request Cruise Pricing
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Cruise Travelers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default Cruise;